/**
 * VendorCompare — Side-by-side vendor comparison dashboard
 * Compares 2-3 vendors on performance, financials, projects, quotes, monthly trends
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft, Award, DollarSign, Briefcase, FileText, TrendingUp,
  BarChart3, User, Building2, Phone, Mail, Crown, CheckCircle2, XCircle
} from 'lucide-react';
import Layout from '@/components/Layout';

const INR = '\u20B9';

// ─── Mini Score Ring ────────────────────────────────────────────────────
function ScoreRing({ total, grade, size = 72, isBest = false }) {
  const colors = { A: '#22c55e', B: '#3b82f6', C: '#f59e0b', D: '#ef4444' };
  const stroke = colors[grade] || colors.D;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox="0 0 36 36" style={{ width: size, height: size }} className="-rotate-90">
        <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
          fill="none" stroke="#e7e5e4" strokeWidth="3" />
        <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
          fill="none" stroke={stroke} strokeWidth="3" strokeDasharray={`${total}, 100`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-base font-bold text-stone-900">{total}</span>
        <span className="text-[8px] text-stone-500">/100</span>
      </div>
      {isBest && (
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
          <Crown size={10} className="text-white" />
        </div>
      )}
    </div>
  );
}

// ─── Comparison Row ─────────────────────────────────────────────────────
function CompRow({ label, icon: Icon, values, format = 'text', highlightMax = false, highlightMin = false }) {
  const numericValues = values.map(v => typeof v === 'number' ? v : 0);
  const maxVal = Math.max(...numericValues);
  const minVal = Math.min(...numericValues);

  return (
    <div className="grid border-b border-stone-100 last:border-0" style={{ gridTemplateColumns: `200px repeat(${values.length}, 1fr)` }}>
      <div className="p-3 bg-stone-50 flex items-center gap-2 border-r border-stone-100">
        {Icon && <Icon size={13} className="text-stone-400 flex-shrink-0" />}
        <span className="text-xs font-medium text-stone-600">{label}</span>
      </div>
      {values.map((val, i) => {
        const isMax = highlightMax && numericValues[i] === maxVal && maxVal > 0;
        const isMin = highlightMin && numericValues[i] === minVal && minVal < maxVal;
        return (
          <div key={i} className={`p-3 text-center border-r border-stone-100 last:border-0 ${isMax ? 'bg-green-50' : isMin ? 'bg-red-50/30' : ''}`}>
            {format === 'currency' ? (
              <span className={`text-sm font-semibold ${isMax ? 'text-green-700' : 'text-stone-800'}`}>
                {INR}{(val || 0).toLocaleString('en-IN')}
              </span>
            ) : format === 'percent' ? (
              <span className={`text-sm font-semibold ${isMax ? 'text-green-700' : 'text-stone-800'}`}>
                {val || 0}%
              </span>
            ) : format === 'number' ? (
              <span className={`text-sm font-semibold ${isMax ? 'text-green-700' : 'text-stone-800'}`}>
                {val || 0}
              </span>
            ) : (
              <span className="text-sm text-stone-700">{val || '-'}</span>
            )}
            {isMax && <Crown size={10} className="inline ml-1 text-amber-500" />}
          </div>
        );
      })}
    </div>
  );
}

// ─── Bar Chart Comparison ───────────────────────────────────────────────
function MonthlyChart({ vendorData }) {
  const colors = ['#1c1917', '#3b82f6', '#22c55e'];
  const allAmounts = vendorData.flatMap(v => v.monthly_expenses.map(m => m.amount));
  const maxAmt = Math.max(...allAmounts, 1);
  const months = vendorData[0]?.monthly_expenses.map(m => m.month.split(' ')[0]) || [];

  return (
    <div data-testid="monthly-comparison-chart">
      {/* Legend */}
      <div className="flex items-center gap-4 mb-4">
        {vendorData.map((v, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: colors[i] }} />
            <span className="text-xs text-stone-600">{v.vendor.name}</span>
          </div>
        ))}
      </div>
      {/* Chart */}
      <div className="flex items-end gap-2 h-40">
        {months.map((month, mi) => (
          <div key={mi} className="flex-1 flex flex-col items-center gap-1">
            <div className="flex items-end gap-0.5 w-full justify-center" style={{ height: '120px' }}>
              {vendorData.map((v, vi) => {
                const amt = v.monthly_expenses[mi]?.amount || 0;
                const pct = amt > 0 ? Math.max(6, (amt / maxAmt) * 100) : 3;
                return (
                  <div key={vi} className="flex-1 max-w-[20px] rounded-t transition-all relative group"
                    style={{ height: `${pct}%`, backgroundColor: amt > 0 ? colors[vi] : '#e7e5e4' }}>
                    {amt > 0 && (
                      <div className="absolute -top-5 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-[9px] font-medium text-stone-700 bg-white px-1 rounded shadow">
                        {INR}{(amt / 1000).toFixed(0)}K
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <span className="text-[10px] text-stone-500">{month}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Vendor Selector ────────────────────────────────────────────────────
function VendorSelector({ allVendors, selectedIds, onChange, slot }) {
  return (
    <Select value={selectedIds[slot] || ''} onValueChange={(val) => {
      const updated = [...selectedIds];
      updated[slot] = val;
      onChange(updated);
    }}>
      <SelectTrigger className="h-9 text-sm" data-testid={`vendor-select-${slot}`}>
        <SelectValue placeholder={`Select Vendor ${slot + 1}`} />
      </SelectTrigger>
      <SelectContent>
        {allVendors.map(v => (
          <SelectItem key={v.id} value={v.id} disabled={selectedIds.includes(v.id) && selectedIds[slot] !== v.id}>
            {v.name} {v.company_name ? `(${v.company_name})` : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────
export default function VendorCompare() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = localStorage.getItem('token');
  const [allVendors, setAllVendors] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vendorCount, setVendorCount] = useState(2);

  // Load vendor list on mount
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const resp = await axios.get(`${API}/vendors/directory`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAllVendors(resp.data || []);
        // Pre-fill from URL
        const urlIds = searchParams.get('ids');
        if (urlIds) {
          const ids = urlIds.split(',').filter(Boolean);
          setSelectedIds(ids);
          setVendorCount(Math.max(2, ids.length));
        }
      } catch { toast.error('Failed to load vendors'); }
    };
    fetchVendors();
  }, []);

  // Fetch comparison when selectedIds has enough values
  useEffect(() => {
    const validIds = selectedIds.filter(Boolean);
    if (validIds.length >= 2) {
      fetchComparison(validIds);
      setSearchParams({ ids: validIds.join(',') }, { replace: true });
    } else {
      setComparisonData(null);
    }
  }, [selectedIds]);

  const fetchComparison = async (ids) => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/vendors/compare`, {
        params: { vendor_ids: ids.join(',') },
        headers: { Authorization: `Bearer ${token}` }
      });
      setComparisonData(resp.data);
    } catch { toast.error('Failed to load comparison'); }
    finally { setLoading(false); }
  };

  const handleChangeIds = (newIds) => {
    setSelectedIds(newIds);
  };

  const vendors = comparisonData?.vendors || [];
  const bestScoreIdx = vendors.length > 0
    ? vendors.reduce((best, v, i) => v.performance_score.total > vendors[best].performance_score.total ? i : best, 0)
    : -1;

  const gradeColors = { A: 'bg-green-50 text-green-700 border-green-200', B: 'bg-blue-50 text-blue-700 border-blue-200', C: 'bg-amber-50 text-amber-700 border-amber-200', D: 'bg-red-50 text-red-700 border-red-200' };

  return (
    <Layout>
    <div className="min-h-[calc(100vh-64px)] bg-stone-50" data-testid="vendor-compare-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-6 py-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/vendors')} className="h-8 w-8" data-testid="back-to-vendors">
            <ArrowLeft size={16} />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-stone-900">Vendor Comparison</h1>
            <p className="text-xs text-stone-500">Compare vendors side-by-side on performance, financials, and project history</p>
          </div>
        </div>
      </div>

      {/* Vendor Selectors */}
      <div className="px-6 py-4">
        <div className="bg-white border border-stone-200 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-sm font-medium text-stone-700">Select Vendors to Compare:</span>
            <div className="flex items-center gap-2">
              <Button
                variant={vendorCount === 2 ? 'default' : 'outline'}
                size="sm" className="h-7 text-xs"
                onClick={() => { setVendorCount(2); setSelectedIds(prev => prev.slice(0, 2)); }}
                data-testid="compare-2-btn"
              >2 Vendors</Button>
              <Button
                variant={vendorCount === 3 ? 'default' : 'outline'}
                size="sm" className="h-7 text-xs"
                onClick={() => { setVendorCount(3); setSelectedIds(prev => [...prev, ''].slice(0, 3)); }}
                data-testid="compare-3-btn"
              >3 Vendors</Button>
            </div>
          </div>
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${vendorCount}, 1fr)` }}>
            {Array.from({ length: vendorCount }).map((_, i) => (
              <VendorSelector key={i} allVendors={allVendors} selectedIds={selectedIds} onChange={handleChangeIds} slot={i} />
            ))}
          </div>
        </div>
      </div>

      {/* Comparison Content */}
      {loading && <div className="p-8 text-center text-stone-400">Loading comparison...</div>}

      {!loading && vendors.length >= 2 && (
        <div className="px-6 pb-8 space-y-6">

          {/* Score Cards */}
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${vendors.length}, 1fr)` }}>
            {vendors.map((v, i) => {
              const isBest = i === bestScoreIdx && v.performance_score.total > 0;
              return (
                <div key={v.vendor.id}
                  className={`rounded-lg p-5 border transition-all ${isBest ? 'border-amber-300 bg-amber-50/40 ring-1 ring-amber-200' : 'border-stone-200 bg-white'}`}
                  data-testid={`score-card-${i}`}
                >
                  <div className="flex items-center gap-4">
                    <ScoreRing total={v.performance_score.total} grade={v.performance_score.grade} isBest={isBest} />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-stone-900 truncate">{v.vendor.name}</p>
                      <p className="text-xs text-stone-500 truncate">{v.vendor.company_name || v.vendor.email}</p>
                      <Badge variant="outline" className={`mt-1.5 text-xs ${gradeColors[v.performance_score.grade] || gradeColors.D}`}>
                        Grade {v.performance_score.grade}
                      </Badge>
                      {isBest && <span className="ml-2 text-[10px] text-amber-600 font-semibold">TOP PERFORMER</span>}
                    </div>
                  </div>
                  {/* Score breakdown bars */}
                  <div className="mt-4 space-y-1.5">
                    {['payment_volume', 'project_breadth', 'quote_engagement', 'consistency'].map(key => {
                      const b = v.performance_score.breakdown[key];
                      const pct = b ? Math.round((b.score / b.max) * 100) : 0;
                      const barColor = v.performance_score.grade === 'A' ? 'bg-green-500' : v.performance_score.grade === 'B' ? 'bg-blue-500' : v.performance_score.grade === 'C' ? 'bg-amber-500' : 'bg-red-400';
                      return (
                        <div key={key} className="flex items-center gap-2">
                          <span className="text-[10px] text-stone-500 w-20 truncate capitalize">{key.replace('_', ' ')}</span>
                          <div className="flex-1 bg-stone-100 rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[10px] text-stone-500 w-6 text-right">{b?.score || 0}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detailed Comparison Table */}
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden" data-testid="comparison-table">
            <div className="bg-stone-900 text-white px-4 py-2.5 grid" style={{ gridTemplateColumns: `200px repeat(${vendors.length}, 1fr)` }}>
              <span className="text-xs font-medium">Metric</span>
              {vendors.map((v, i) => (
                <span key={i} className="text-xs font-medium text-center truncate px-2">{v.vendor.name}</span>
              ))}
            </div>

            {/* Profile Section */}
            <div className="bg-stone-100 px-4 py-1.5 grid border-b border-stone-200" style={{ gridTemplateColumns: `200px repeat(${vendors.length}, 1fr)` }}>
              <span className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide flex items-center gap-1"><User size={10} /> Profile</span>
            </div>
            <CompRow label="Company" icon={Building2} values={vendors.map(v => v.vendor.company_name || '-')} />
            <CompRow label="Email" icon={Mail} values={vendors.map(v => v.vendor.email || '-')} />
            <CompRow label="Phone" icon={Phone} values={vendors.map(v => v.vendor.phone || '-')} />
            <CompRow label="GSTIN" icon={FileText} values={vendors.map(v => v.vendor.gst_number || '-')} />
            <CompRow label="Since" values={vendors.map(v => v.vendor.created_at ? formatDate(new Date(v.vendor.created_at), { shortMonth: true }) : '-')} />

            {/* Financial Section */}
            <div className="bg-stone-100 px-4 py-1.5 grid border-b border-stone-200 border-t" style={{ gridTemplateColumns: `200px repeat(${vendors.length}, 1fr)` }}>
              <span className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide flex items-center gap-1"><DollarSign size={10} /> Financials</span>
            </div>
            <CompRow label="Total Paid" icon={DollarSign} values={vendors.map(v => v.financials.total_paid)} format="currency" highlightMax />
            <CompRow label="Payment Count" values={vendors.map(v => v.financials.payment_count)} format="number" highlightMax />
            <CompRow label="Avg Payment" values={vendors.map(v => v.financials.avg_payment)} format="currency" highlightMax />
            <CompRow label="Projects Count" icon={Briefcase} values={vendors.map(v => v.financials.projects_count)} format="number" highlightMax />

            {/* Quote Section */}
            <div className="bg-stone-100 px-4 py-1.5 grid border-b border-stone-200 border-t" style={{ gridTemplateColumns: `200px repeat(${vendors.length}, 1fr)` }}>
              <span className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide flex items-center gap-1"><FileText size={10} /> Quotes</span>
            </div>
            <CompRow label="Total Submissions" icon={FileText} values={vendors.map(v => v.quotes.total)} format="number" highlightMax />
            <CompRow label="Accepted" icon={CheckCircle2} values={vendors.map(v => v.quotes.accepted)} format="number" highlightMax />
            <CompRow label="Acceptance Rate" icon={TrendingUp} values={vendors.map(v => v.quotes.acceptance_rate)} format="percent" highlightMax />

            {/* Performance Section */}
            <div className="bg-stone-100 px-4 py-1.5 grid border-b border-stone-200 border-t" style={{ gridTemplateColumns: `200px repeat(${vendors.length}, 1fr)` }}>
              <span className="text-[10px] font-semibold text-stone-500 uppercase tracking-wide flex items-center gap-1"><Award size={10} /> Performance Score</span>
            </div>
            <CompRow label="Overall Score" icon={Award} values={vendors.map(v => v.performance_score.total)} format="number" highlightMax />
            <CompRow label="Grade" values={vendors.map(v => `Grade ${v.performance_score.grade}`)} />
            <CompRow label="Payment Volume" values={vendors.map(v => v.performance_score.breakdown.payment_volume.score)} format="number" highlightMax />
            <CompRow label="Project Breadth" values={vendors.map(v => v.performance_score.breakdown.project_breadth.score)} format="number" highlightMax />
            <CompRow label="Quote Engagement" values={vendors.map(v => v.performance_score.breakdown.quote_engagement.score)} format="number" highlightMax />
            <CompRow label="Consistency" values={vendors.map(v => v.performance_score.breakdown.consistency.score)} format="number" highlightMax />
          </div>

          {/* Projects Worked */}
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden" data-testid="projects-comparison">
            <div className="bg-stone-900 text-white px-4 py-2.5">
              <span className="text-xs font-medium flex items-center gap-1"><Briefcase size={12} /> Projects Worked</span>
            </div>
            <div className="grid divide-x divide-stone-200" style={{ gridTemplateColumns: `repeat(${vendors.length}, 1fr)` }}>
              {vendors.map((v, i) => (
                <div key={i} className="p-3">
                  <p className="text-xs font-medium text-stone-500 mb-2">{v.vendor.name}</p>
                  {v.projects_worked.length > 0 ? (
                    <div className="space-y-1.5">
                      {v.projects_worked.map((p, pi) => (
                        <div key={pi} className="flex items-center justify-between text-xs">
                          <span className="text-stone-700 truncate flex-1 mr-2">{p.project_name}</span>
                          <span className="font-semibold text-stone-900 flex-shrink-0">{INR}{p.total_paid.toLocaleString('en-IN')}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-stone-400">No projects</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Monthly Expense Trend */}
          <div className="bg-white border border-stone-200 rounded-lg p-4" data-testid="monthly-trend">
            <h3 className="font-semibold text-stone-800 text-sm mb-4 flex items-center gap-2">
              <BarChart3 size={14} /> Monthly Expense Trend (Last 6 Months)
            </h3>
            <MonthlyChart vendorData={vendors} />
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && vendors.length < 2 && (
        <div className="px-6 py-16 text-center">
          <BarChart3 size={48} className="mx-auto mb-3 text-stone-300" />
          <p className="text-stone-500 font-medium">Select at least 2 vendors to compare</p>
          <p className="text-sm text-stone-400 mt-1">Choose vendors from the dropdowns above</p>
        </div>
      )}
    </div>
    </Layout>
  );
}
